import { useMemo, useState } from "react";
import FeatherIcon from "feather-icons-react";
import {
  CALENDAR_RULE_NOTE,
  currentCycle,
  formatCycleDate,
  upcomingCycles,
} from "./payablesCalendar";

const BLUE = "#2563EB";
const BLUE_DARK = "#1D4ED8";

/**
 * Monthly Payables Calendar for the invoice form header.
 *
 * The whole upcoming schedule is always on screen in a compact table, with the
 * cycle this invoice lands in summarised above it. Hovering overlays a larger,
 * easier-to-read copy — an overlay rather than an in-flow resize, so the form
 * below never shifts.
 */
const PayablesCalendarPanel = ({ months = 12 }) => {
  const [zoomed, setZoomed] = useState(false);

  // Only runs still to come — a payment date already passed is not actionable.
  const rows = useMemo(() => upcomingCycles({ months }), [months]);
  const active = useMemo(() => currentCycle(), []);

  // Two periods is enough to orient at a glance and keeps the header workable on
  // narrow screens; hovering reveals the rest.
  const preview = rows.slice(0, 2);

  const table = (data, big) => {
    const th = {
      textAlign: "left", padding: big ? "6px 10px" : "3px 7px",
      fontSize: big ? "0.62rem" : "0.54rem", fontWeight: 800, color: "#64748B",
      textTransform: "uppercase", letterSpacing: "0.4px", whiteSpace: "nowrap",
      borderBottom: "1px solid #E7EAF0", background: "#F8FAFC",
      position: "sticky", top: 0, zIndex: 1,
    };
    const td = {
      padding: big ? "5px 10px" : "2px 7px",
      fontSize: big ? "0.75rem" : "0.64rem",
      color: "#0F172A", borderBottom: "1px solid #F1F5F9", whiteSpace: "nowrap",
      fontVariantNumeric: "tabular-nums",
    };
    return (
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={th}>Month</th>
            <th style={th}>Submit By</th>
            <th style={th}>Approve By</th>
            <th style={{ ...th, background: "#FEF9C3" }}>Payment Date</th>
          </tr>
        </thead>
        <tbody>
          {data.map((c) => {
            const isActive = active && c.paymentDate === active.paymentDate;
            return (
              <tr key={c.paymentDate} style={{ background: isActive ? "#EFF6FF" : "transparent" }}>
                <td style={{ ...td, fontWeight: 700 }}>{c.month}</td>
                <td style={td}>{formatCycleDate(c.submitBy)}</td>
                <td style={td}>{formatCycleDate(c.approveBy)}</td>
                <td style={{ ...td, fontWeight: 700, color: isActive ? BLUE_DARK : "#0F172A" }}>
                  {formatCycleDate(c.paymentDate)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div
      style={{
        // Sits hard right in the header, immediately left of the close button.
        // The header centres its children vertically, so leaving this centred too
        // keeps the chip level with the close mark.
        position: "relative", marginLeft: "auto", marginRight: 10,
        flex: "0 1 320px", minWidth: 230, maxWidth: 320,
      }}
      onMouseEnter={() => setZoomed(true)}
      onMouseLeave={() => setZoomed(false)}
    >
      {/* Always-visible summary of the cycle this invoice falls into. */}
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
        <FeatherIcon icon="calendar" size={13} color={BLUE} />
        <span style={{
          fontSize: "0.56rem", fontWeight: 800, letterSpacing: "0.6px",
          color: "#94A3B8", textTransform: "uppercase",
        }}>
          Payables Calendar
        </span>
        {active && (
          <span style={{ marginLeft: "auto", fontSize: "0.72rem", fontWeight: 800, color: BLUE_DARK }}>
            Pays {formatCycleDate(active.paymentDate)}
          </span>
        )}
      </div>

      {/* The next two periods, always on screen. */}
      <div
        tabIndex={0}
        onFocus={() => setZoomed(true)}
        onBlur={() => setZoomed(false)}
        style={{ border: "1px solid #E2E8F0", borderRadius: 8, overflow: "hidden", background: "#fff" }}
      >
        {table(preview, false)}
      </div>
      {rows.length > preview.length && !zoomed && (
        <div style={{
          fontSize: "0.58rem", color: "#94A3B8", fontWeight: 600,
          textAlign: "right", marginTop: 2,
        }}>
          Hover for all {rows.length} periods
        </div>
      )}

      {/* Hover overlay — the full schedule, larger. Overlaid rather than resized
          in place so the form below never shifts. */}
      {zoomed && (
        <div
          style={{
            position: "absolute", top: 20, right: 0, zIndex: 30,
            width: "min(500px, calc(100vw - 80px))",
            background: "#fff", border: "1px solid #CBD5E1", borderRadius: 12,
            boxShadow: "0 20px 48px rgba(15,23,42,0.22)", overflow: "hidden",
          }}
        >
          <div style={{ maxHeight: 300, overflowY: "auto" }}>{table(rows, true)}</div>
          <div style={{
            fontSize: "0.66rem", color: "#92400E", lineHeight: 1.5,
            background: "#FFFBEB", borderTop: "1px solid #FDE68A", padding: "7px 11px",
          }}>
            {CALENDAR_RULE_NOTE}
          </div>
        </div>
      )}
    </div>
  );
};

export { PayablesCalendarPanel };
